/**
 * Usage Hooks
 *
 * React Query hooks for usage tracking and billing data
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * Types
 */

export interface UsageSummary {
  totalTokens: number;
  totalCost: number;
  byModel: Record<string, { tokens: number; cost: number }>;
  periodStart: string;
  periodEnd: string;
  limit?: number;
  percentUsed?: number;
}

export interface UsageEvent {
  id: string;
  eventType: string;
  resourceId: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface UsageByModel {
  modelId: string;
  provider: string | null;
  totalTokens: number;
  inputTokens: number;
  outputTokens: number;
  totalCost: number;
  requestCount: number;
}

/**
 * API Response Types
 */

interface CurrentUsageResponse extends UsageSummary {}

interface UsageHistoryResponse {
  workspaceId: string;
  startDate: string;
  endDate: string;
  data: UsageEvent[];
  pageInfo?: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    startCursor: string | null;
    endCursor: string | null;
  };
}

interface UsageByModelResponse {
  workspaceId: string;
  byModel: UsageByModel[];
}

/**
 * Hook: useCurrentUsage
 *
 * Fetch current billing period usage summary with token and cost breakdown
 */
export function useCurrentUsage(workspaceId: string | null): UseQueryResult<UsageSummary> {
  return useQuery<UsageSummary>({
    queryKey: ['usage', 'current', workspaceId],
    queryFn: async () => {
      if (!workspaceId) throw new Error('Workspace ID is required');
      const response = await api.get<CurrentUsageResponse>(
        `/usage/current?workspaceId=${workspaceId}`
      );
      return response;
    },
    enabled: !!workspaceId,
    staleTime: 60000, // Cache for 1 minute
    refetchInterval: 60000, // Refetch every 1 minute
  });
}

/**
 * Hook: useUsageHistory
 *
 * Fetch historical usage events within a date range
 */
export function useUsageHistory(
  workspaceId: string | null,
  startDate: Date,
  endDate: Date
): UseQueryResult<UsageEvent[]> {
  return useQuery<UsageEvent[]>({
    queryKey: ['usage', 'history', workspaceId, startDate.toISOString(), endDate.toISOString()],
    queryFn: async () => {
      if (!workspaceId) return [];
      const response = await api.get<UsageHistoryResponse>(
        `/usage/history?workspaceId=${workspaceId}&startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`
      );
      return response.data || [];
    },
    enabled: !!workspaceId,
    staleTime: 60000, // Cache for 1 minute
  });
}

/**
 * Hook: useUsageByModel
 *
 * Fetch usage breakdown by model for current period
 */
export function useUsageByModel(
  workspaceId: string | null
): UseQueryResult<UsageByModel[]> {
  return useQuery<UsageByModel[]>({
    queryKey: ['usage', 'by-model', workspaceId],
    queryFn: async () => {
      if (!workspaceId) return [];
      const response = await api.get<UsageByModelResponse>(
        `/usage/by-model?workspaceId=${workspaceId}`
      );
      return response.byModel;
    },
    enabled: !!workspaceId,
    staleTime: 60000, // Cache for 1 minute
    refetchInterval: 60000, // Refetch every 1 minute
  });
}
